import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { format, parseISO } from "date-fns";
import {
  InputAdornment,
  MenuItem,
  Typography,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useRevalidator } from "react-router-dom";


import { isFormComplete } from "../../../../../../components/ui library/helpers/functions";
import ApiRequestErrorHandler from "../../../../../../components/global/ApiRequestErrorHandler";
import Modal from "../../../../../../components/global/Modal";
import ResponseModal from "../../../../../../components/global/ResponseModal";
import useService from "../../../../../../hooks/useService";
import Colors from "../../../../../../components/global/Colors";

export default function AddPayment({
  activeItem,
  setActiveItem,
  open,
  setOpen,
  checkTypeOptions,
  entityOpts,
  caseDetail,
}) {
  const settlementCheckRepository = useService('repositories/settlement-check-repository');
  const { id: casePk } = useParams();
  const [formData, setFormData] = useState({
    paidBy: "",
    paidTo: "",
    checkNumber: "",
    amount: "",
    checkDate: "",
    checkType: "",
  });
  const [responseModal, setResponseModal] = useState(false);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { revalidate } = useRevalidator();
  const allowSave = isFormComplete(formData, ["checkNumber"]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const {checkDate} = formData;
      const body = {
        ...formData,
        checkDate: format(new Date(checkDate), "yyyy-MM-dd"),
      };
      if (activeItem) {
        await settlementCheckRepository.patch(activeItem.id, body)
      } else {
        await settlementCheckRepository.createRecord({
          ...body,
          case: +casePk,
        });
      }
      revalidate();
      setLoading(false);
      setOpen(false);
      setResponseModal(true);
      setResponseBreakdown("Successfully added payment information");
    } catch (error) {
      setLoading(false);
      const res = error.response.data;
      const errArr = ApiRequestErrorHandler(res);
      setIsError(true);
      setResponseModal(true);
      setResponseBreakdown(errArr);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({...prev, [field]: value}))
  }

  const handleRest = () => {
    setFormData({
      paidBy: "",
      paidTo: "",
      checkNumber: "",
      amount: "",
      checkDate: "",
      checkType: "",
    });
    setActiveItem(null)
  };

  const handleClose = () => {
    setOpen(false);
    handleRest();
  };

  const closeResModal = () => {
    handleRest();
    setResponseModal(false);
    setResponseBreakdown("");
    if (isError) {
      setTimeout(() => {
        setIsError(false);
      }, "1000");
    }
  };

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  useEffect(() => {
    if (!activeItem) {
      return;
    } else {
      const {checkNumber, amount, checkDate, checkType, paidTo, paidBy} = activeItem;

      setFormData({
        checkNumber,
        amount,
        checkDate: parseISO(checkDate),
        checkType,
        paidTo: paidTo.id,
        paidBy: paidBy.id,
      });
    }
  }, [open, activeItem]);

  return (
    <>
      <Modal
        open={open}
        title={
          <Typography 
            fontSize={24} 
            fontWeight={700} 
            color={Colors.blue[700]} 
          >
            {activeItem ? "Edit Payment" : "Add Payment"}
          </Typography>
        }
        onClose={handleClose}
        actions={[
          {
            disabled: !allowSave,
            isLoading: loading,
            onClick: handleSubmit,
            label: "Submit Payment",
            className: "bg-blue-600 text-white hover:bg-blue-700 disabled:text-gray-500 disabled:bg-gray-200",
          },
        ]}
      >
        <div className="flex flex-col items-center w-full p-0">
          <div className="flex flex-wrap items-center w-full py-6">
            <div className="flex w-full">
              <Typography variant="h6" className="text-lg font-bold w-fit">
                Law Firm Name:
              </Typography>
              <Typography variant="h6" className="ml-2 text-lg w-fit">
                {caseDetail.lawFirmName ?? ""}
              </Typography>
            </div>
            <div className="flex w-full">
              <Typography variant="h6" className="text-lg font-bold w-fit">
              Medical Amount:
              </Typography>
              <Typography variant="h6" className="ml-2 text-lg w-fit">
                {formatter.format(caseDetail.medPayAmount)}
              </Typography>
            </div>
            <div className="flex w-full">
              <Typography variant="h6" className="text-lg font-bold w-fit">
              Settlement Amount:
              </Typography>
              <Typography variant="h6" className="ml-2 text-lg w-fit">
                {formatter.format(caseDetail.actualTotalSettlementAmount)}
              </Typography>
            </div>
          </div>

          <TextField
            className="w-full mb-6"
            id="outlined-select-currency"
            label="Payment Type"
            onChange={(event) => {
              handleChange("checkType", event.target.value);
            }}
            required
            select
            size="small"
            value={formData.checkType}
          >
            {checkTypeOptions.map((option) => (
              <MenuItem value={option.value} key={option.value} >
                {option.display_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            className="w-full mb-6"
            id="outlined-select-currency"
            label="Payment From"
            onChange={(event) => {
              handleChange("paidBy", event.target.value);
            }}
            required
            select
            size="small"
            value={formData.paidBy}
          >
            {
              entityOpts
              .filter((entity) => entity.id !== formData.paidTo)
              .map((option) => (
                <MenuItem value={option.id} key={option.id} >
                  {option.name}
                </MenuItem>
              ))
            }
          </TextField>

          <TextField
            className="w-full mb-6"
            id="outlined-select-currency"
            label="Payment To"
            onChange={(event) => {
              handleChange("paidTo", event.target.value);
            }}
            required
            select
            size="small"
            value={formData.paidTo}
          >
            {
              entityOpts
              .filter((entity) => entity.id !== formData.paidBy)
              .map((option) => (
                <MenuItem value={option.id} key={option.id} >
                  {option.name}
                </MenuItem>
              ))
            }
          </TextField>

          <TextField
            className="w-full mb-6"
            id="outlined-start-adornment"
            InputProps={{
              startAdornment: <InputAdornment position="start">#</InputAdornment>,
            }}
            label="Payment Identification"
            onChange={(e) => handleChange("checkNumber",e.target.value)}
            size="small"
            value={formData.checkNumber}
          />

          <TextField
            required
            className="w-full mb-6"
            id="outlined-start-adornment"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            label="Payment Amount"
            onChange={(e) => handleChange("amount",e.target.value)}
            size="small"
            type="number"
            value={formData.amount}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              className="w-full"
              label="Payment Date"
              value={formData.checkDate}
              onChange={(newValue) => {
                handleChange("checkDate", newValue);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  error: false,
                  required: true,
                },
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </div>
      </Modal>
      <ResponseModal
        title={`${isError ? "Failed" : "Successful"}`}
        isError={isError}
        description={isError ? "" : responseBreakdown}
        openBool={responseModal}
        setOpenBool={setResponseModal}
        errorMessage={responseBreakdown}
        handleCloseFunc={closeResModal}
      />
    </>
  );
}